import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IContract } from 'features/inventory/contracts/types';
import { sectorService } from 'features/settings/topology/api/sector.service';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  equipmentService,
  examService,
  laboratoryService,
  locationService,
  macroExamService,
  workstationService
} from 'shared/services';
import { operationalUnitService } from 'features/settings/topology/api/operational-unit.service';
import { Option, OptionDescription, StatusOption } from 'shared/types/general';
import { warehouseService } from '../../api/warehouse.service';
import {
  IdentityField,
  AutocompleteAdapter,
  TextFieldAdapter,
  MultipleAutocompleteAdapter
} from 'components/enhanced';
import { SectionHeader } from 'components/ui/typography';

interface FormParams {
  onSubmit: any;
  products: Option[];
  categories: Option[];
  statusOptions: StatusOption[];
  contracts: Option[];
  labelOptions: Option[];
  initialContract?: IContract;
  warehouseId?: number | null;
  initialValues?: any;
}
function ProductWarehouseForm(params: FormParams) {
  const {
    labelOptions,
    statusOptions,
    products,
    categories,
    contracts,
    onSubmit,
    initialContract,
    warehouseId,
    initialValues
  } = params;
  const { t } = useTranslation();
  const translationPrefix = 'pages.warehouse.new.misc_product_warehouse_form';
  const hasInitialProduct = initialValues?.product !== undefined;
  const [warehouseCode, setWarehouseCode] = useState<string>('');
  const [laboratories, setLaboratories] = useState<OptionDescription[]>([]);
  const [sectors, setSectors] = useState<OptionDescription[]>([]);
  const [operationalUnits, setOperationalUnits] = useState<OptionDescription[]>([]);
  const [workstations, setWorkstations] = useState<OptionDescription[]>([]);
  const [exams, setExams] = useState<OptionDescription[]>([]);
  const [locations, setLocations] = useState<Option[]>([]);
  const [equipments, setEquipments] = useState<OptionDescription[]>([]);
  const [macroExamOptions, setMacroExamOptions] = useState<string[]>([]);

  useEffect(() => {
    laboratoryService.getAllBaseInformation().then(setLaboratories);
    sectorService.getAllBaseInformation().then(setSectors);
    operationalUnitService.getAllBaseInformation().then(setOperationalUnits);
    workstationService.getAllBaseInformation().then(setWorkstations);
    examService.getAllBaseInformation().then(setExams);
    locationService.getAllBaseInformation().then(setLocations);
    equipmentService.getAllBaseInformation().then(setEquipments);
    macroExamService.getAllBaseInformation().then(setMacroExamOptions);
  }, []);

  useEffect(() => {
    if (!warehouseId) return;
    warehouseService.get(warehouseId).then((res) => {
      if (res) {
        setWarehouseCode(res.code);
      }
    });
  }, [warehouseId]);

  const handleSubmit = async (values: any) => {
    const productValue = initialValues?.product;

    const updatedValues = {
      ...values,
      product: hasInitialProduct ? productValue : values.product
    };

    onSubmit(updatedValues);
  };

  return (
    <EnhancedForm
      onSubmit={handleSubmit}
      formId="addProductThreshold"
      hideControls
      initialValues={initialValues}
      autoStyleLayout
      columns={12}>
      <>
        <SectionHeader
          title={`${t('global.warehouse')}: ${warehouseCode ?? ''}`}
          subtitle={t(`${translationPrefix}.warehouse-subtitle`)}
          gridSpan
        />
        {!hasInitialProduct && products && (
          <IdentityField
            gridSpan={12}
            name="product"
            component={AutocompleteAdapter}
            options={products}
            optionLabel="description"
            label={t('global.product')}
          />
        )}

        <IdentityField
          gridSpan={6}
          name="category"
          label={t('global.category')}
          component={AutocompleteAdapter}
          options={categories}
        />

        <IdentityField
          gridSpan={6}
          name="status"
          label={t(`entities.product.status`)}
          component={AutocompleteAdapter}
          options={statusOptions}
          placeholder="Status"
          type="text"
          optionLabel="display_name"
          optionValue="value"
        />

        <IdentityField
          gridSpan={3}
          name="label"
          component={AutocompleteAdapter}
          options={labelOptions}
          label={t('global.label')}
        />

        <IdentityField
          gridSpan={3}
          name="understock_threshold"
          component={TextFieldAdapter}
          label={t('entities.product.understock_threshold')}
          type="number"
        />

        <IdentityField
          gridSpan={3}
          name="overstock_threshold"
          component={TextFieldAdapter}
          label={t('entities.product.overstock_threshold')}
          type="number"
        />

        <IdentityField
          gridSpan={3}
          name="restock_threshold"
          component={TextFieldAdapter}
          label={t('entities.product.restock_threshold')}
          type="number"
        />

        <IdentityField
          gridSpan={12}
          name="notes"
          component={TextFieldAdapter}
          label={t('global.notes')}
          type="text"
        />

        <SectionHeader
          title={t(`${translationPrefix}.locations-section`)}
          subtitle={t(`${translationPrefix}.locations-subtitle`)}
          gridSpan
        />

        <IdentityField
          gridSpan={12}
          name="locations"
          component={MultipleAutocompleteAdapter}
          options={locations}
          label={t('global.locations')}
          type="text"
        />

        {!initialContract && (
          <>
            <SectionHeader
              title={t('actions.manage_prices')}
              subtitle={t(`${translationPrefix}.prices-subtitle`)}
              gridSpan
            />

            <IdentityField
              gridSpan={12}
              name="contract_header"
              component={AutocompleteAdapter}
              options={contracts}
              label={t('global.contract')}
              initialContract={initialContract}
            />

            <IdentityField
              gridSpan={6}
              name="unit_price"
              component={TextFieldAdapter}
              type="number"
              label={t(`entities.contract_line.unit_price`)}
            />

            <IdentityField
              gridSpan={6}
              name="initial_available_units"
              component={TextFieldAdapter}
              type="number"
              label={t(`entities.contract_line.initial_available_units`)}
            />

            <IdentityField
              gridSpan={6}
              name="initial_discounted_units"
              component={TextFieldAdapter}
              type="number"
              label={t('entities.contract_line.initial_discounted_units')}
            />

            <IdentityField
              gridSpan={6}
              name="iva"
              component={TextFieldAdapter}
              type="number"
              label={t('entities.contract_line.iva')}
            />
          </>
        )}

        <SectionHeader
          title={t('global.costs')}
          subtitle={t(`${translationPrefix}.costs-subtitle`)}
          gridSpan
        />
        <IdentityField
          gridSpan={4}
          name="laboratories"
          label={t('global.laboratories')}
          component={MultipleAutocompleteAdapter}
          options={laboratories}
        />

        <IdentityField
          gridSpan={4}
          name="operational_units"
          label={t('global.operational_units')}
          component={MultipleAutocompleteAdapter}
          options={operationalUnits}
        />

        <IdentityField
          gridSpan={4}
          name="sectors"
          label={t('global.sectors')}
          component={MultipleAutocompleteAdapter}
          options={sectors}
        />

        <IdentityField
          gridSpan={6}
          name="workstations"
          label={t('global.workstations')}
          component={MultipleAutocompleteAdapter}
          options={workstations}
        />

        <IdentityField
          gridSpan={6}
          name="equipments"
          label={t('global.equipments')}
          component={MultipleAutocompleteAdapter}
          options={equipments}
          optionLabel={'description'}
        />

        <IdentityField
          gridSpan={6}
          name="macro_exams"
          component={MultipleAutocompleteAdapter}
          options={macroExamOptions}
          label={t('global.macro-exams')}
        />

        <IdentityField
          gridSpan={6}
          name="exams"
          component={MultipleAutocompleteAdapter}
          options={exams}
          label={t('global.exams')}
          optionLabel={'description'}
        />
      </>
    </EnhancedForm>
  );
}

export default ProductWarehouseForm;
